import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ref, update } from 'firebase/database';
import { database } from '../../firebase/firebase';
import { productService } from '../../services/productService';
import { certificationService } from '../../services/certificationService';
import { notificationService } from '../../services/notificationService';
import { qrCodeService } from '../../services/qrCodeService';

// Async thunks
export const fetchPendingProducts = createAsyncThunk(
  'productCertification/fetchPendingProducts',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔄 Fetching pending products...');
      const products = await productService.getPendingProducts();
      console.log('✅ Pending products fetched:', products.length);
      return products;
    } catch (error) {
      console.error('❌ Error fetching pending products:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchApprovedCertifications = createAsyncThunk(
  'productCertification/fetchApprovedCertifications',
  async (currentUserId, { rejectWithValue }) => {
    try {
      console.log('🔄 Fetching approved certifications for user:', currentUserId);
      
      if (!currentUserId) {
        console.warn('⚠️ No user ID provided for fetching certifications');
        return [];
      }
      
      const certifications = await certificationService.getApprovedCertificationsByCurrentCertifier(currentUserId);
      console.log('✅ Approved certifications fetched:', certifications.length);
      return certifications;
    } catch (error) {
      console.error('❌ Error fetching approved certifications:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCertificationDetails = createAsyncThunk(
  'productCertification/fetchCertificationDetails',
  async (certificationId, { rejectWithValue }) => {
    try {
      console.log('🔄 Fetching certification details for:', certificationId);
      const certification = await certificationService.getCertificationById(certificationId);
      
      if (!certification) {
        throw new Error('Certification not found');
      }
      
      console.log('✅ Certification details fetched');
      return certification;
    } catch (error) {
      console.error('❌ Error fetching certification details:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const approveProduct = createAsyncThunk(
  'productCertification/approveProduct',
  async ({ product, certificationExpiry, currentUser }, { rejectWithValue }) => {
    try {
      console.log('🚀 Starting product approval process for product:', product.id);
      
      // Validate required fields
      if (!certificationExpiry) {
        throw new Error('Certification expiry date is required');
      }
      
      if (!currentUser?.uid) {
        throw new Error('User authentication required');
      }

      // Step 1: Create certification record
      console.log('📝 Creating certification record...');
      const certificationId = await certificationService.createCertification(product.id);
      console.log('✅ Certification created with ID:', certificationId);
      
      // Step 2: Generate QR code
      console.log('🔳 Generating QR code...');
      const qrData = JSON.stringify({
        productId: product.id,
        productName: product.product_name,
        status: 'Approved',
        certificationExpiry
      });
      
      const qrCode = await qrCodeService.generateCustomQRCode(qrData);
      console.log('✅ QR code generated successfully');
      
      // Step 3: Approve certification
      console.log('📋 Approving certification...');
      await certificationService.approveCertification(
        certificationId,
        product,
        currentUser,
        {
          certificationBody: product.product_manufacturer || 'Default Certification Body',
          certificationExpiryDate: certificationExpiry,
          certificationNotes: 'Product approved through certification process'
        }
      );
      console.log('✅ Certification approved successfully');

      // Step 4: Prepare product history entry
      console.log('📊 Preparing product history...');
      const historyEntry = {
        action: 'Product Certification Approved',
        action_date: Date.now(),
        action_by: currentUser.uid,
      };

      // Step 5: Update product in database
      console.log('💾 Updating product in database...');
      const productRef = ref(database, `products/${product.id}`);
      
      const updateData = {
        product_qr_code: qrCode,
        product_updated_at: Date.now(),
        product_certification_status: 'Approved',
        product_history: [...(product.product_history || []), historyEntry]
      };
      
      await update(productRef, updateData);
      console.log('✅ Product updated successfully in Firebase');

      // Step 6: Log success
      console.log('🎉 Product approval process completed successfully!');
      return product.id;

    } catch (error) {
      console.error('❌ Error in approveProduct:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const rejectProduct = createAsyncThunk(
  'productCertification/rejectProduct',
  async ({ product, rejectionReason, currentUser }, { rejectWithValue }) => {
    try {
      console.log('🚨 Starting product rejection process for product:', product.id);
      
      // Validate required fields
      if (!rejectionReason) {
        throw new Error('Rejection reason is required');
      }
      
      if (!currentUser?.uid) {
        throw new Error('User authentication required');
      }

      const certificationId = await certificationService.createCertification(product.id);
      
      await certificationService.rejectCertification(
        certificationId,
        product,
        currentUser,
        {
          rejectionReason,
          certificationBody: product.product_manufacturer || 'Default Certification Body',
          certificationNotes: rejectionReason
        }
      );

      // Add to product history for rejection
      const historyEntry = {
        action: 'Product Certification Rejected',
        action_date: Date.now(),
        action_by: currentUser.uid,
        rejection_reason: rejectionReason
      };

      const productRef = ref(database, `products/${product.id}`);
      await update(productRef, {
        product_updated_at: Date.now(),
        product_certification_status: 'Rejected',
        product_history: [...(product.product_history || []), historyEntry]
      });

      console.log('✅ Product rejected successfully');
      return product.id;
    } catch (error) {
      console.error('❌ Error in rejectProduct:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const requestProductUpdate = createAsyncThunk(
  'productCertification/requestProductUpdate',
  async ({ product, updateRequestMessage }, { rejectWithValue }) => {
    try {
      console.log('📧 Requesting product update for product:', product.id);
      
      if (!updateRequestMessage) {
        throw new Error('Update request message is required');
      }

      await notificationService.createNotification({
        notification_user_id: product.product_factory_id,
        notification_title: "Update Required",
        notification_message: `Your product "${product.product_name}" requires additional information: ${updateRequestMessage}`,
        notification_type: "warning",
        notification_is_read: false,
        notification_action_url: '/Edit_Products',
        notification_priority: "Medium",
        notification_created_at: new Date().toISOString(),
        notification_expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      });

      console.log('✅ Update request sent successfully');
      return product.id;
    } catch (error) {
      console.error('❌ Error in requestProductUpdate:', error);
      return rejectWithValue(error.message);
    }
  }
);

const productCertificationSlice = createSlice({
  name: 'productCertification',
  initialState: {
    // Arrays - always initialized as empty arrays
    pendingProducts: [],
    approvedCertifications: [],
    supportingDocs: [],
    
    // Objects - always initialized as null
    selectedProduct: null,
    selectedCertification: null,
    
    // Strings - always initialized as empty strings
    rejectionReason: '',
    certificationExpiry: '',
    error: '',
    success: '',
    updateRequestMessage: '',
    
    // Booleans - always initialized as false
    loading: false,
    isRequestingUpdate: false,
    showApprovedCertifications: false
  },
  reducers: {
    setSelectedProduct: (state, action) => {
      state.selectedProduct = action.payload;
      if (action.payload) {
        state.rejectionReason = action.payload.rejectionReason || '';
        state.certificationExpiry = action.payload.certification_expiry || '';
        state.isRequestingUpdate = false;
      }
    },
    setSelectedCertification: (state, action) => {
      state.selectedCertification = action.payload;
    },
    setRejectionReason: (state, action) => {
      state.rejectionReason = action.payload;
    },
    setCertificationExpiry: (state, action) => {
      state.certificationExpiry = action.payload;
    },
    setSupportingDocs: (state, action) => {
      state.supportingDocs = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    setUpdateRequestMessage: (state, action) => {
      state.updateRequestMessage = action.payload;
    },
    setIsRequestingUpdate: (state, action) => {
      state.isRequestingUpdate = action.payload;
    },
    setShowApprovedCertifications: (state, action) => {
      state.showApprovedCertifications = action.payload;
    },
    clearMessages: (state) => {
      state.error = '';
      state.success = '';
    },
    clearSelectedCertification: (state) => {
      state.selectedCertification = null;
    },
    clearAllSelection: (state) => {
      state.selectedProduct = null;
      state.selectedCertification = null;
      state.rejectionReason = '';
      state.certificationExpiry = '';
      state.supportingDocs = [];
      state.isRequestingUpdate = false;
      state.updateRequestMessage = '';
    },
    handleFileSelection: (state, action) => {
      const files = action.payload;
      state.supportingDocs = Array.from(files).map(file => ({
        name: file.name,
        type: file.type,
        size: file.size,
      }));
    },
    // Reset entire state to initial values
    resetCertificationState: (state) => {
      return {
        pendingProducts: [],
        approvedCertifications: [],
        supportingDocs: [],
        selectedProduct: null,
        selectedCertification: null,
        rejectionReason: '',
        certificationExpiry: '',
        error: '',
        success: '',
        updateRequestMessage: '',
        loading: false,
        isRequestingUpdate: false,
        showApprovedCertifications: false
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch pending products
      .addCase(fetchPendingProducts.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchPendingProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingProducts = action.payload || [];
      })
      .addCase(fetchPendingProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch pending products';
        state.pendingProducts = [];
      })
      
      // Fetch approved certifications
      .addCase(fetchApprovedCertifications.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchApprovedCertifications.fulfilled, (state, action) => {
        state.loading = false;
        state.approvedCertifications = action.payload || [];
      })
      .addCase(fetchApprovedCertifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch approved certifications';
        state.approvedCertifications = [];
      })
      
      // Fetch certification details
      .addCase(fetchCertificationDetails.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchCertificationDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedCertification = action.payload;
      })
      .addCase(fetchCertificationDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch certification details';
        state.selectedCertification = null;
      })
      
      // Approve product
      .addCase(approveProduct.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(approveProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'Product approved successfully!';
        state.pendingProducts = state.pendingProducts.filter(p => p.id !== action.payload);
        state.selectedProduct = null;
        state.supportingDocs = [];
        state.rejectionReason = '';
        state.certificationExpiry = '';
      })
      .addCase(approveProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = 'Error in approval process: ' + (action.payload || 'Unknown error');
      })
      
      // Reject product
      .addCase(rejectProduct.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(rejectProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'Product rejected successfully!';
        state.pendingProducts = state.pendingProducts.filter(p => p.id !== action.payload);
        state.selectedProduct = null;
        state.supportingDocs = [];
        state.rejectionReason = '';
        state.certificationExpiry = '';
      })
      .addCase(rejectProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = 'Error rejecting product: ' + (action.payload || 'Unknown error');
      })
      
      // Request update
      .addCase(requestProductUpdate.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(requestProductUpdate.fulfilled, (state, action) => {
        state.loading = false;
        state.success = 'Update request sent successfully!';
        state.pendingProducts = state.pendingProducts.filter(p => p.id !== action.payload);
        state.selectedProduct = null;
        state.supportingDocs = [];
        state.updateRequestMessage = '';
        state.isRequestingUpdate = false;
      })
      .addCase(requestProductUpdate.rejected, (state, action) => {
        state.loading = false;
        state.error = 'Error requesting update: ' + (action.payload || 'Unknown error');
      });
  }
});

export const {
  setSelectedProduct,
  setSelectedCertification,
  setRejectionReason,
  setCertificationExpiry,
  setSupportingDocs,
  setError,
  setSuccess,
  setUpdateRequestMessage,
  setIsRequestingUpdate,
  setShowApprovedCertifications,
  clearMessages,
  clearSelectedCertification,
  clearAllSelection,
  handleFileSelection,
  resetCertificationState
} = productCertificationSlice.actions;

export default productCertificationSlice.reducer;